import React, { useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';

import { palette } from '@lib/styles/palette';

import Icon from '@components/common/Icon';

import Chart from 'chart.js/auto';

interface ChartGraphProp {
  averagePrices:
    | {
        date: typeof Date;
        price: number;
        properMaxPrice: number;
        properMinPrice: number;
        standardPrice: number;
        stdDevPrice: number;
      }[]
    | undefined;
}

function ChartGraph({ averagePrices }: ChartGraphProp) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tootipContents, setTooltipContents] = useState<{
    title: string;
    content: string;
  }>({ title: '', content: '' });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipXpos, setTooltipXpos] = useState(0);
  const [tooltipYpos, setTooltipYpos] = useState(0);

  const priceInfos = averagePrices?.map(({ price }) => price) ?? [0, 0, 0, 0];
  const maxPrice = Math.max(...priceInfos);
  const stepSize = Math.floor(maxPrice / 4);

  useEffect(() => {
    if (!averagePrices) return;
    if (canvasRef.current && tooltipRef.current) {
      const chart = new Chart(canvasRef.current, {
        type: 'line',
        data: {
          labels: ['3개월 전', '2개월 전', '1개월 전', '오늘'],
          datasets: [
            {
              data: priceInfos,
              borderColor: `${palette.orange9}`,
              backgroundColor: `${palette.orange9}`,
              pointRadius: 1,
              pointHoverBackgroundColor: `${palette.orange9}`,
              pointHoverBorderColor: `${palette.orange1}`,
              pointHoverBorderWidth: 5,
              pointHoverRadius: 7,
              cubicInterpolationMode: 'monotone',
            },
          ],
        },
        options: {
          onHover: (event) => {
            const { y } = event;

            if (y === null) return;

            setTooltipYpos(y);
          },
          responsive: false,
          maintainAspectRatio: false,
          interaction: {
            mode: 'nearest',
            intersect: false,
            axis: 'x',
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: (context) => {
                const { chart, tooltip } = context;

                if (!tooltipRef.current) return;

                if (tooltip.opacity === 0) {
                  setIsTooltipVisible(false);
                  return;
                }

                const [title] = tooltip.title;
                const [
                  {
                    lines: [content],
                  },
                ] = tooltip.body;

                setIsTooltipVisible(true);
                setTooltipContents({ title, content });

                let offset = 0;
                const tooltipWidth = tooltipRef.current.clientWidth;
                if (chart.width / 2 > tooltip.caretX) {
                  offset = 20;
                } else {
                  offset -= tooltipWidth + 10;
                }
                setTooltipXpos(tooltip.caretX + offset);
              },
            },
          },
          scales: {
            x: {
              offset: true,
              grid: {
                display: false,
                offset: true,
              },
            },
            y: {
              beginAtZero: true,
              suggestedMax: maxPrice + stepSize,
              grid: {
                drawBorder: false,
                borderDash: [2],
                tickColor: 'transparent',
                color: function (context) {
                  if (context.tick.value > 0) {
                    return `${palette.gray0}`;
                  } else {
                    return 'transparent';
                  }
                },
              },
              ticks: {
                display: false,
                stepSize,
              },
            },
          },
        },
        plugins: [
          {
            id: '0',
            afterDraw: (chart) => {
              let x, yAxis, ctx;
              if (chart.tooltip && chart.tooltip?.opacity !== 0) {
                x = chart.tooltip.caretX;
                yAxis = chart.scales.y;
                ctx = chart.ctx;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, yAxis.top);
                ctx.lineTo(x, yAxis.bottom);
                ctx.lineWidth = 1;
                ctx.strokeStyle = `${palette.orange9}`;
                ctx.stroke();
                ctx.restore();
              }
            },
          },
        ],
      });

      chart.setActiveElements([
        {
          datasetIndex: 0,
          index: 3,
        },
      ]);

      const [activeElement] = chart.getActiveElements();
      const { element } = activeElement;
      const { x, y } = element.tooltipPosition(true);

      const { tooltip, chartArea } = chart;
      tooltip?.setActiveElements(
        [
          {
            datasetIndex: 0,
            index: 3,
          },
        ],
        {
          x: 0,
          y: 0,
        },
      );
      const tooltipX = x;
      const tooltipY = y + 20 >= 170 ? 160 : y + 20;

      setTooltipXpos(tooltipX);
      setTooltipYpos(tooltipY);

      chart.update();
    }
  }, []);

  useEffect(() => {
    if (tooltipRef.current && tootipContents.content) {
      const width = tooltipRef.current.clientWidth;

      setTooltipXpos((prev) => prev - width - 5);
    }
  }, [tooltipRef.current]);

  if (!averagePrices) {
    return (
      <div css={fallbackChartStyle}>
        <Icon icon="noResult" />
        <h2>시세 데이터가 아직 충분하지 않아요</h2>
      </div>
    );
  }

  return (
    <div css={canvasContainerStyle} className="canvas-container">
      <canvas
        id="searchGraph"
        ref={canvasRef}
        style={{
          display: 'inline-block',
          width: '329px',
          height: '160px',
        }}
      ></canvas>
      <div
        css={customTooltipStyle(tooltipXpos, tooltipYpos, isTooltipVisible)}
        ref={tooltipRef}
      >
        {tootipContents.title}
        <span className="content">{tootipContents.content}원</span>
      </div>
    </div>
  );
}

const fallbackChartStyle = css`
  width: 100%;
  height: 269px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  h2 {
    margin-top: 30px;
  }

  ::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${palette.black10};
    opacity: 0.05;
    border-radius: 4px;
  }
`;

const canvasContainerStyle = css`
  position: relative;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }

  /** mobile */
  @media (max-width: 599px) {
    align-self: center;
    width: 100%;
    height: 70%;

    canvas {
      width: 100% !important;
      height: 100% !important;
    }
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    align-self: center;
    flex: 1 1 auto;
    margin: 0;
    width: 100%;
    height: 80%;

    canvas {
      width: 100% !important;
      height: 100% !important;
    }
  }
`;

const customTooltipStyle = (
  left: number,
  top: number,
  isTooltipVisible: boolean,
) => css`
  background: rgba(255, 105, 0, 0.1);
  border-radius: 5px;
  color: ${palette.orange9};
  display: flex;
  justify-content: center;
  align-items: baseline;
  padding: 6px 5px 6px;
  pointer-events: none;
  position: absolute;
  transition: all 0.1s ease;
  left: ${left}px;
  top: ${top}px;
  font-size: 12px;
  opacity: ${isTooltipVisible ? 1 : 0};

  .content {
    padding: 0;
    display: inline-block;
    margin-left: 4px;
    font-size: 16px;
  }
`;

export default ChartGraph;
